import { useState, useEffect, useRef, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { ObjectUploader } from "@/components/ObjectUploader";
import {
  MessageSquare,
  Send,
  Plus,
  Search,
  ArrowLeft,
  Users,
  Loader2,
  Paperclip,
  AlertTriangle,
  Zap,
  Smartphone,
  Check,
} from "lucide-react";
import { initials, avatarSwatch } from "@/components/backstage/lib";

// ---------------------------------------------------------------------------
// Types (mirror server/lib/messenger contracts)
// ---------------------------------------------------------------------------
type Settings = {
  workspaceId: string;
  userId: string;
  enabled: boolean;
  smsFallbackEnabled: boolean;
  notifyOnNewMessage: boolean;
};

type DirectoryEntry = {
  userId: string;
  name: string;
  email: string | null;
  profileImageUrl: string | null;
  role: string | null;
  messagingEnabled: boolean;
  phoneE164: string | null;
  talentId: string | null;
  talentName: string | null;
};

type Participant = {
  id: string;
  userId: string | null;
  phoneE164: string | null;
  talentId: string | null;
  displayName: string | null;
  profileImageUrl: string | null;
  isSelf: boolean;
};

type Conversation = {
  id: string;
  kind: string;
  title: string | null;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  unreadCount: number;
  participants: Participant[];
};

type Attachment = {
  objectPath: string;
  name?: string;
  contentType?: string;
  size?: number;
};

type Message = {
  id: string;
  conversationId: string;
  senderUserId: string | null;
  channel: "internal" | "sms" | string;
  body: string;
  attachments: Attachment[] | null;
  deliveryStatus: string;
  failureReason: string | null;
  createdAt: string;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function otherParticipants(c: Conversation): Participant[] {
  return c.participants.filter((p) => !p.isSelf);
}

function conversationTitle(c: Conversation): string {
  if (c.kind === "group") {
    if (c.title) return c.title;
    const names = otherParticipants(c)
      .map((p) => p.displayName || p.phoneE164 || "Member")
      .filter(Boolean);
    return names.slice(0, 3).join(", ") || "Group";
  }
  const o = otherParticipants(c)[0];
  return o?.displayName || o?.phoneE164 || "Conversation";
}

function conversationAvatar(c: Conversation): { url: string | null; seed: string } {
  const o = otherParticipants(c)[0];
  return { url: o?.profileImageUrl || null, seed: conversationTitle(c) };
}

function formatTime(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (diffDays < 7) return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function isImage(a: Attachment): boolean {
  return (a.contentType || "").startsWith("image/");
}

// ---------------------------------------------------------------------------
// Opt-in gate
// ---------------------------------------------------------------------------
function OptInGate({ onEnable, enabling }: { onEnable: () => void; enabling: boolean }) {
  return (
    <div className="h-full flex items-center justify-center" data-testid="messenger-optin-gate">
      <div className="max-w-md text-center space-y-4 px-6">
        <div className="mx-auto h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
          <MessageSquare className="h-7 w-7 text-primary" />
        </div>
        <h2 className="text-lg font-semibold">Turn on Messages</h2>
        <p className="text-sm text-muted-foreground">
          Chat directly with anyone in your workspace. Messages to teammates with Messages
          turned on are delivered instantly and free; everyone else is reached automatically
          by text.
        </p>
        <Button onClick={onEnable} disabled={enabling} data-testid="button-messenger-enable">
          {enabling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
          Turn on Messages
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// New conversation dialog
// ---------------------------------------------------------------------------
function NewConversationDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: (conversationId: string) => void;
}) {
  const { toast } = useToast();
  const [mode, setMode] = useState<"dm" | "group">("dm");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [groupTitle, setGroupTitle] = useState("");

  const { data: directory = [], isLoading } = useQuery<DirectoryEntry[]>({
    queryKey: ["/api/messenger/directory"],
    enabled: open,
  });

  useEffect(() => {
    if (!open) {
      setMode("dm");
      setSearch("");
      setSelected([]);
      setGroupTitle("");
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return directory;
    return directory.filter(
      (d) =>
        d.name.toLowerCase().includes(q) ||
        (d.email || "").toLowerCase().includes(q) ||
        (d.talentName || "").toLowerCase().includes(q),
    );
  }, [directory, search]);

  const createMutation = useMutation({
    mutationFn: async (body: any) => {
      const res = await apiRequest("POST", "/api/messenger/conversations", body);
      return (await res.json()) as Conversation;
    },
    onSuccess: (conv) => {
      queryClient.invalidateQueries({ queryKey: ["/api/messenger/conversations"] });
      onCreated(conv.id);
      onOpenChange(false);
    },
    onError: (e: any) => {
      toast({ title: "Couldn't start conversation", description: e?.message, variant: "destructive" });
    },
  });

  function toggle(userId: string) {
    if (mode === "dm") {
      createMutation.mutate({ kind: "dm", targetUserId: userId });
      return;
    }
    setSelected((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]));
  }

  function createGroup() {
    if (selected.length < 1) {
      toast({ title: "Pick at least one person", variant: "destructive" });
      return;
    }
    createMutation.mutate({ kind: "group", title: groupTitle.trim() || "Group", memberUserIds: selected });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="dialog-new-conversation">
        <DialogHeader>
          <DialogTitle>New message</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-1 rounded-md border p-0.5">
          <button
            onClick={() => setMode("dm")}
            data-testid="newconv-mode-dm"
            className={cn(
              "flex-1 px-3 py-1.5 text-sm font-medium rounded transition-colors",
              mode === "dm" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            Direct
          </button>
          <button
            onClick={() => setMode("group")}
            data-testid="newconv-mode-group"
            className={cn(
              "flex-1 px-3 py-1.5 text-sm font-medium rounded transition-colors",
              mode === "group" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            Group
          </button>
        </div>

        {mode === "group" && (
          <Input
            placeholder="Group name (optional)"
            value={groupTitle}
            onChange={(e) => setGroupTitle(e.target.value)}
            data-testid="input-group-title"
          />
        )}

        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search people…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
            data-testid="input-directory-search"
          />
        </div>

        <ScrollArea className="h-64 -mx-2 px-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No people found.</p>
          ) : (
            <div className="space-y-0.5">
              {filtered.map((d) => {
                const isSel = selected.includes(d.userId);
                return (
                  <button
                    key={d.userId}
                    onClick={() => toggle(d.userId)}
                    disabled={createMutation.isPending}
                    data-testid={`directory-row-${d.userId}`}
                    className={cn(
                      "w-full flex items-center gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-muted",
                      isSel && "bg-muted",
                    )}
                  >
                    <Avatar className="h-9 w-9">
                      {d.profileImageUrl && <AvatarImage src={d.profileImageUrl} />}
                      <AvatarFallback className={avatarSwatch(d.name)}>{initials(d.name)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">{d.name}</span>
                        {d.messagingEnabled ? (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-primary">
                            <Zap className="h-3 w-3" />Free
                          </span>
                        ) : d.phoneE164 ? (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-muted-foreground">
                            <Smartphone className="h-3 w-3" />SMS
                          </span>
                        ) : null}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {d.talentName || d.email || d.role || ""}
                      </div>
                    </div>
                    {mode === "group" && isSel && <Check className="h-4 w-4 text-primary shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {mode === "group" && (
          <DialogFooter>
            <Button
              onClick={createGroup}
              disabled={createMutation.isPending || selected.length < 1}
              data-testid="button-create-group"
            >
              {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Create group ({selected.length})
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Conversation list
// ---------------------------------------------------------------------------
function ConversationList({
  conversations,
  loading,
  activeId,
  onSelect,
  onNew,
}: {
  conversations: Conversation[];
  loading: boolean;
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
}) {
  return (
    <div className="flex flex-col h-full border-r" data-testid="messenger-conversation-list">
      <div className="shrink-0 flex items-center justify-between px-3 py-2.5 border-b">
        <h2 className="text-sm font-semibold">Messages</h2>
        <Button size="sm" variant="ghost" onClick={onNew} data-testid="button-new-conversation">
          <Plus className="h-4 w-4 mr-1" />
          New
        </Button>
      </div>
      <ScrollArea className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center px-6 py-10 space-y-2">
            <MessageSquare className="h-8 w-8 mx-auto text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">No conversations yet.</p>
            <Button size="sm" variant="outline" onClick={onNew} data-testid="button-empty-new-conversation">
              Start a conversation
            </Button>
          </div>
        ) : (
          <div className="py-1">
            {conversations.map((c) => {
              const av = conversationAvatar(c);
              const isGroup = c.kind === "group";
              const active = c.id === activeId;
              return (
                <button
                  key={c.id}
                  onClick={() => onSelect(c.id)}
                  data-testid={`conversation-row-${c.id}`}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted",
                    active && "bg-muted",
                  )}
                >
                  <Avatar className="h-10 w-10 shrink-0">
                    {av.url && <AvatarImage src={av.url} />}
                    <AvatarFallback className={isGroup ? "bg-muted-foreground/20" : avatarSwatch(av.seed)}>
                      {isGroup ? <Users className="h-4 w-4" /> : initials(av.seed)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className={cn("text-sm truncate", c.unreadCount > 0 ? "font-semibold" : "font-medium")}>
                        {conversationTitle(c)}
                      </span>
                      <span className="text-[11px] text-muted-foreground shrink-0">
                        {formatTime(c.lastMessageAt)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className={cn(
                          "text-xs truncate",
                          c.unreadCount > 0 ? "text-foreground font-medium" : "text-muted-foreground",
                        )}
                      >
                        {c.lastMessagePreview || "No messages yet"}
                      </span>
                      {c.unreadCount > 0 && (
                        <span
                          className="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold flex items-center justify-center"
                          data-testid={`unread-badge-${c.id}`}
                        >
                          {c.unreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Message bubble
// ---------------------------------------------------------------------------
function MessageBubble({ m, mine }: { m: Message; mine: boolean }) {
  const isSms = m.channel === "sms";
  // Brand colors only: green = free/internal lane, graphite = SMS lane.
  const mineCls = isSms
    ? "bg-[hsl(var(--graphite))] text-white"
    : "bg-primary text-primary-foreground";
  const theirsCls = isSms
    ? "bg-muted text-foreground"
    : "bg-[hsl(var(--green-soft))] text-foreground dark:bg-primary/20";
  const failed = m.deliveryStatus === "failed";

  return (
    <div className={cn("flex flex-col max-w-[78%]", mine ? "items-end ml-auto" : "items-start mr-auto")}>
      <div
        className={cn("rounded-2xl px-3.5 py-2 text-sm break-words", mine ? mineCls : theirsCls)}
        data-testid={`message-bubble-${m.id}`}
      >
        {m.attachments && m.attachments.length > 0 && (
          <div className="space-y-1.5 mb-1">
            {m.attachments.map((a, i) =>
              isImage(a) ? (
                <a key={i} href={a.objectPath} target="_blank" rel="noreferrer">
                  <img
                    src={a.objectPath}
                    alt={a.name || "attachment"}
                    className="rounded-lg max-h-56 object-cover"
                  />
                </a>
              ) : (
                <a
                  key={i}
                  href={a.objectPath}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 underline opacity-90"
                >
                  <Paperclip className="h-3.5 w-3.5" />
                  {a.name || "Attachment"}
                </a>
              ),
            )}
          </div>
        )}
        {m.body && <span className="whitespace-pre-wrap">{m.body}</span>}
      </div>
      <div className="flex items-center gap-1.5 mt-0.5 px-1">
        <span className="text-[10px] text-muted-foreground">{formatTime(m.createdAt)}</span>
        <span className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5">
          {isSms ? <Smartphone className="h-2.5 w-2.5" /> : <Zap className="h-2.5 w-2.5" />}
          {isSms ? "SMS" : "Free"}
        </span>
        {failed && (
          <span className="text-[10px] text-destructive inline-flex items-center gap-0.5" title={m.failureReason || ""}>
            <AlertTriangle className="h-2.5 w-2.5" />
            Failed
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Thread view (header + messages + composer)
// ---------------------------------------------------------------------------
function ThreadView({
  conversationId,
  conversation,
  onBack,
  myUserId,
}: {
  conversationId: string;
  conversation: Conversation | undefined;
  onBack: () => void;
  myUserId: string | undefined;
}) {
  const { toast } = useToast();
  const [draft, setDraft] = useState("");
  const [pendingAttachments, setPendingAttachments] = useState<Attachment[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const { data: messages = [], isLoading } = useQuery<Message[]>({
    queryKey: ["/api/messenger/conversations", conversationId, "messages"],
    refetchInterval: 4000,
  });

  // Mark read on open and whenever new inbound messages arrive.
  const lastCountRef = useRef(0);
  useEffect(() => {
    if (!conversationId) return;
    if (messages.length !== lastCountRef.current) {
      lastCountRef.current = messages.length;
      apiRequest("POST", `/api/messenger/conversations/${conversationId}/read`, {})
        .then(() => queryClient.invalidateQueries({ queryKey: ["/api/messenger/conversations"] }))
        .then(() => queryClient.invalidateQueries({ queryKey: ["/api/messenger/unread-count"] }))
        .catch(() => {});
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.length, conversationId]);

  const sendMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/messenger/conversations/${conversationId}/messages`, {
        body: draft.trim(),
        attachments: pendingAttachments,
      });
      return (await res.json()) as Message;
    },
    onSuccess: () => {
      setDraft("");
      setPendingAttachments([]);
      queryClient.invalidateQueries({ queryKey: ["/api/messenger/conversations", conversationId, "messages"] });
      queryClient.invalidateQueries({ queryKey: ["/api/messenger/conversations"] });
    },
    onError: (e: any) => {
      toast({ title: "Message not sent", description: e?.message, variant: "destructive" });
    },
  });

  function handleSend() {
    if (!draft.trim() && pendingAttachments.length === 0) return;
    sendMutation.mutate();
  }

  const title = conversation ? conversationTitle(conversation) : "Conversation";
  const isGroup = conversation?.kind === "group";
  const subtitle = conversation
    ? isGroup
      ? `${conversation.participants.length} members`
      : (() => {
          const o = otherParticipants(conversation)[0];
          if (!o) return "";
          if (o.userId) return o.displayName || "";
          return o.phoneE164 ? `Texting ${o.phoneE164}` : "";
        })()
    : "";

  return (
    <div className="flex flex-col h-full" data-testid="messenger-thread-view">
      {/* Header */}
      <div className="shrink-0 flex items-center gap-3 px-3 py-2.5 border-b">
        <Button
          size="icon"
          variant="ghost"
          className="md:hidden shrink-0"
          onClick={onBack}
          data-testid="button-thread-back"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <Avatar className="h-8 w-8 shrink-0">
          {conversation && conversationAvatar(conversation).url && (
            <AvatarImage src={conversationAvatar(conversation).url!} />
          )}
          <AvatarFallback className={isGroup ? "bg-muted-foreground/20" : avatarSwatch(title)}>
            {isGroup ? <Users className="h-4 w-4" /> : initials(title)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate" data-testid="thread-title">{title}</div>
          {subtitle && <div className="text-xs text-muted-foreground truncate">{subtitle}</div>}
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 px-3 py-3" ref={scrollRef as any}>
        {isLoading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-10">
            No messages yet. Say hello 👋
          </p>
        ) : (
          <div className="space-y-2">
            {messages.map((m) => (
              <MessageBubble key={m.id} m={m} mine={!!myUserId && m.senderUserId === myUserId} />
            ))}
            <div ref={bottomRef} />
          </div>
        )}
      </ScrollArea>

      {/* Composer */}
      <div className="shrink-0 border-t p-2.5 space-y-2">
        {pendingAttachments.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="composer-attachments">
            {pendingAttachments.map((a, i) => (
              <div
                key={i}
                className="flex items-center gap-1.5 text-xs bg-muted rounded-md px-2 py-1"
              >
                <Paperclip className="h-3 w-3" />
                <span className="max-w-[140px] truncate">{a.name || "Attachment"}</span>
                <button
                  onClick={() => setPendingAttachments((prev) => prev.filter((_, idx) => idx !== i))}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid={`remove-attachment-${i}`}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <ObjectUploader
            maxNumberOfFiles={5}
            maxFileSize={10 * 1024 * 1024}
            buttonClassName="h-9 w-9 p-0 shrink-0 bg-transparent text-muted-foreground hover:text-foreground hover:bg-muted shadow-none"
            onGetUploadParameters={async (file) => {
              const res = await apiRequest("POST", "/api/messenger/upload-url", {
                name: file.name,
                size: file.size,
                contentType: file.type || "application/octet-stream",
              });
              const data = await res.json();
              (file as any).meta = {
                ...((file as any).meta || {}),
                objectPath: data.objectPath,
              };
              return { method: "PUT" as const, url: data.uploadURL };
            }}
            onComplete={(result) => {
              const added: Attachment[] = [];
              for (const f of result.successful || []) {
                const objectPath = (f as any).meta?.objectPath as string | undefined;
                if (!objectPath) continue;
                added.push({
                  objectPath,
                  name: f.name || "attachment",
                  contentType: f.type || undefined,
                  size: f.size || undefined,
                });
              }
              if (added.length) setPendingAttachments((prev) => [...prev, ...added]);
            }}
          >
            <Paperclip className="h-4 w-4" />
          </ObjectUploader>
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Message…"
            rows={1}
            className="resize-none min-h-9 max-h-32"
            data-testid="input-message-body"
          />
          <Button
            size="icon"
            className="h-9 w-9 shrink-0"
            onClick={handleSend}
            disabled={sendMutation.isPending || (!draft.trim() && pendingAttachments.length === 0)}
            data-testid="button-send-message"
          >
            {sendMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------
export function MessengerPanel() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);

  const { data: settings, isLoading: settingsLoading } = useQuery<Settings>({
    queryKey: ["/api/messenger/settings"],
  });

  const enabled = settings?.enabled ?? false;

  const { data: conversations = [], isLoading: convLoading } = useQuery<Conversation[]>({
    queryKey: ["/api/messenger/conversations"],
    enabled,
    refetchInterval: enabled ? 15000 : false,
  });

  const enableMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PATCH", "/api/messenger/settings", { enabled: true });
      return (await res.json()) as Settings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/messenger/settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/messenger/conversations"] });
    },
    onError: (e: any) => {
      toast({ title: "Couldn't enable Messages", description: e?.message, variant: "destructive" });
    },
  });

  const activeConversation = conversations.find((c) => c.id === activeId);

  if (settingsLoading) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (!enabled) {
    return <OptInGate onEnable={() => enableMutation.mutate()} enabling={enableMutation.isPending} />;
  }

  return (
    <div className="h-full rounded-lg border overflow-hidden" data-testid="messenger-panel">
      <div className="grid h-full md:grid-cols-[320px_1fr]">
        {/* List: full width on mobile when no thread; hidden when a thread is open */}
        <div className={cn("h-full min-h-0", activeId ? "hidden md:block" : "block")}>
          <ConversationList
            conversations={conversations}
            loading={convLoading}
            activeId={activeId}
            onSelect={setActiveId}
            onNew={() => setNewOpen(true)}
          />
        </div>

        {/* Thread: full width on mobile when open; always shown on desktop */}
        <div className={cn("h-full min-h-0", activeId ? "block" : "hidden md:block")}>
          {activeId ? (
            <ThreadView
              key={activeId}
              conversationId={activeId}
              conversation={activeConversation}
              onBack={() => setActiveId(null)}
              myUserId={user?.id}
            />
          ) : (
            <div className="h-full hidden md:flex items-center justify-center text-muted-foreground">
              <div className="text-center space-y-2">
                <MessageSquare className="h-10 w-10 mx-auto text-muted-foreground/40" />
                <p className="text-sm">Select a conversation or start a new one.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <NewConversationDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        onCreated={(id) => setActiveId(id)}
      />
    </div>
  );
}
